import type { RevenueAnalyticsAction } from './actions';
import { initialRevenueAnalyticsState } from './types';
import type { RevenueAnalyticsState } from './types';

/**
 * Pure transitions for the Revenue Analytics view state. Selection,
 * page size, and search changes all snap the page back to 1 so a
 * `skip` never outruns a freshly narrowed result set.
 */
export function revenueAnalyticsReducer(
    state: RevenueAnalyticsState,
    action: RevenueAnalyticsAction,
): RevenueAnalyticsState {
    switch (action.type) {
        case 'SET_SCOPE': {
            if (state.firmId === action.firmId && state.superUserMode === action.superUserMode) return state;
            // A scope change invalidates the selected provider and paging. An in-flight CSV
            // export survives the reset so the button stays disabled until it settles.
            return {
                ...initialRevenueAnalyticsState,
                firmId: action.firmId,
                superUserMode: action.superUserMode,
                isDownloadingCsv: state.isDownloadingCsv,
            };
        }
        case 'SELECT_PROVIDER': {
            if (state.selectedProviderId === action.providerId) return state;
            // A new provider starts fresh: reset paging and clear any search.
            return {
                ...state,
                selectedProviderId: action.providerId,
                page: 1,
                symbolSearchInput: '',
                appliedSymbolSearch: '',
            };
        }
        case 'SET_PAGE':
            return { ...state, page: Math.max(1, Math.trunc(action.page)) };
        case 'SET_ROWS_PER_PAGE':
            if (state.rowsPerPage === action.rowsPerPage) return state;
            return { ...state, rowsPerPage: action.rowsPerPage, page: 1 };
        case 'TOGGLE_SORT': {
            // Cycle asc → desc → backend default; a different column starts at asc.
            const current = state.sort;
            const sort =
                current?.column !== action.column
                    ? ({ column: action.column, direction: 'asc' } as const)
                    : current.direction === 'asc'
                      ? ({ column: action.column, direction: 'desc' } as const)
                      : null;
            return { ...state, sort, page: 1 };
        }
        case 'SET_SYMBOL_SEARCH_INPUT':
            return { ...state, symbolSearchInput: action.value };
        case 'APPLY_SYMBOL_SEARCH': {
            // Normalize the input to the committed (trimmed) term so a
            // whitespace-only entry doesn't leave stale text in the box.
            const applied = state.symbolSearchInput.trim();
            if (applied === state.appliedSymbolSearch && applied === state.symbolSearchInput) return state;
            return { ...state, symbolSearchInput: applied, appliedSymbolSearch: applied, page: 1 };
        }
        case 'CLEAR_SYMBOL_SEARCH':
            if (state.symbolSearchInput === '' && state.appliedSymbolSearch === '') return state;
            return { ...state, symbolSearchInput: '', appliedSymbolSearch: '', page: 1 };
        case 'START_CSV_DOWNLOAD':
            if (state.isDownloadingCsv) return state;
            return { ...state, isDownloadingCsv: true, csvDownloadFailed: false };
        case 'FAIL_CSV_DOWNLOAD':
            // Also clear the in-flight flag so the spinner and the error can never coexist.
            if (state.csvDownloadFailed && !state.isDownloadingCsv) return state;
            return { ...state, csvDownloadFailed: true, isDownloadingCsv: false };
        case 'END_CSV_DOWNLOAD':
            if (!state.isDownloadingCsv) return state;
            return { ...state, isDownloadingCsv: false };
    }
}
